import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Skeleton, IconButton, Collapse, useMediaQuery, useTheme } from '@mui/material';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import SyncIcon from '@mui/icons-material/Sync';
import UndoIcon from '@mui/icons-material/Undo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { statusConfig } from '../shared/StatusBadge';

const STATE_DEFS = {
  quote: { icon: RequestQuoteOutlinedIcon, color: statusConfig.quote.color, bg: statusConfig.quote.bg },
  planned: { icon: EventOutlinedIcon, color: statusConfig.planned.color, bg: statusConfig.planned.bg },
  vehicle_in_workshop: { icon: DirectionsCarOutlinedIcon, color: statusConfig.vehicle_in_workshop.color, bg: statusConfig.vehicle_in_workshop.bg },
  in_progress: { icon: PlayArrowOutlinedIcon, color: statusConfig.in_progress.color, bg: statusConfig.in_progress.bg },
  waiting: { icon: PauseOutlinedIcon, color: statusConfig.waiting.color, bg: statusConfig.waiting.bg },
  completed: { icon: CheckCircleOutlinedIcon, color: statusConfig.completed.color, bg: statusConfig.completed.bg },
  picked_up: { icon: LocalShippingOutlinedIcon, color: statusConfig.picked_up.color, bg: statusConfig.picked_up.bg },
  cancelled: { icon: CancelOutlinedIcon, color: statusConfig.cancelled.color, bg: statusConfig.cancelled.bg },
};

const CANONICAL_ORDER = [
  'quote', 'planned', 'vehicle_in_workshop', 'in_progress',
  'waiting', 'completed', 'picked_up', 'cancelled'
];

const STORAGE_KEY = 'verstaani_timeline_collapsed';

function activeWorkMs(events, now) {
  let total = 0;
  for (let i = 0; i < events.length; i++) {
    if (events[i].state === 'in_progress') {
      const endAt = events[i + 1] ? events[i + 1].at : now;
      total += endAt - events[i].at;
    }
  }
  return total;
}

function timePerState(events, now) {
  const result = {};
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    const endAt = events[i + 1] ? events[i + 1].at : now;
    result[e.state] = (result[e.state] || 0) + (endAt - e.at);
  }
  return result;
}

function formatDurationLong(ms, t) {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(totalMin / (60 * 24));
  const h = Math.floor((totalMin % (60 * 24)) / 60);
  const m = totalMin % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}${t('d')}`);
  if (h > 0) parts.push(`${h}${t('h')}`);
  if (m > 0 && d === 0) parts.push(`${m}${t('min')}`);
  return parts.join(' ') || `0${t('min')}`;
}

function shortDateTime(date) {
  const d = date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' });
  const time = date.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
  return `${d} klo ${time}`;
}

function relativeFromNow(date, now, t) {
  const diff = (now - date) / 1000;
  if (diff < 60) return t('timeline.justNow');
  if (diff < 3600) return t('timeline.minutesAgo', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('timeline.hoursAgo', { count: Math.floor(diff / 3600) });
  return t('timeline.daysAgo', { count: Math.floor(diff / 86400) });
}

export default function WorkOrderTimeline({ maintenanceId, maintenanceState }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch { return false; }
  });
  const [hovered, setHovered] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!maintenanceId) return;
    setLoading(true);
    axios.get(`/api/maintenances/${maintenanceId}/timeline/`)
      .then(res => {
        setEvents(res.data.events.map(e => ({ ...e, at: new Date(e.at) })));
      })
      .catch(() => setEvents(null))
      .finally(() => setLoading(false));
  }, [maintenanceId, maintenanceState]);

  // Live ticker — update `now` every 30 seconds so durations stay current
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  if (loading && !events) {
    return <Skeleton variant="rounded" height={60} sx={{ mb: 3, borderRadius: '14px' }} />;
  }

  if (!events || events.length === 0) return null;

  const current = events[events.length - 1];
  const currentDef = STATE_DEFS[current.state] || STATE_DEFS.planned;
  const isTerminalState = ['picked_up', 'cancelled'].includes(current.state);
  const effectiveNow = isTerminalState ? current.at : now;
  const totalMs = effectiveNow - events[0].at;
  const inCurrentMs = now - current.at;
  const activeMs = activeWorkMs(events, effectiveNow);
  const perState = timePerState(events, effectiveNow);
  const visitCount = {};
  events.forEach(e => { visitCount[e.state] = (visitCount[e.state] || 0) + 1; });

  const segments = events.map((e, i) => {
    const start = e.at;
    const end = events[i + 1] ? events[i + 1].at : effectiveNow;
    return { ...e, start, end, duration: Math.max(end - start, 1) };
  });

  const workPeriodCount = visitCount['in_progress'] || 0;

  const activeWorkDuration = formatDurationLong(activeMs, t);

  return (
    <Paper
      elevation={0}
      data-testid="work-order-timeline"
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, md: 2.75 },
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: collapsed ? 'none' : '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
        }}
        onClick={toggleCollapsed}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <TimelineIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
          <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, flexShrink: 0, lineHeight: '20px' }}>{t('timeline.title')}</Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, overflow: 'hidden', height: 20 }}>
            {!isTerminalState && (
              <>
                <HourglassTopIcon sx={{ fontSize: 14, color: currentDef.color, flexShrink: 0, position: 'relative', top: '1px' }} />
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap', lineHeight: '20px' }}>
                  {t(`status.${current.state}`)} {formatDurationLong(inCurrentMs, t)}
                </Typography>
                <Typography component="span" sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0, lineHeight: '20px' }}>·</Typography>
              </>
            )}
            <ScheduleIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0, position: 'relative', top: '1px' }} />
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap', lineHeight: '20px' }}>
              {t('timeline.workTimeShort')} {activeWorkDuration}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {/* Current state chip */}
          <Box
            data-testid="timeline-current-state"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              bgcolor: currentDef.bg,
              color: currentDef.color,
            }}
          >
            <Box sx={{ position: 'relative', width: 8, height: 8 }}>
              <Box
                sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: currentDef.color, position: 'absolute',
                }}
              />
              {!['completed', 'picked_up', 'cancelled'].includes(current.state) && (
                <Box
                  sx={{
                    position: 'absolute', inset: -3, borderRadius: '50%',
                    border: `2px solid ${currentDef.color}`,
                    animation: 'tlPulse 1800ms ease-out infinite',
                    '@keyframes tlPulse': {
                      '0%': { transform: 'scale(1)', opacity: 0.6 },
                      '100%': { transform: 'scale(2)', opacity: 0 },
                    },
                  }}
                />
              )}
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {t(`status.${current.state}`)}
            </Typography>
          </Box>
          <IconButton size="small" sx={{ ml: 0.5 }}>
            {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={!collapsed}>
        {/* Ribbon */}
        <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 3, pb: 2.25 }}>
          {/* Date labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, fontSize: 10, color: 'text.disabled' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {events[0].at.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' })}.
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {t('timeline.now')}
            </Typography>
          </Box>

          {/* Bar */}
          <Box
            data-testid="timeline-ribbon"
            sx={{
              position: 'relative',
              height: isMobile ? 36 : 48,
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              bgcolor: 'divider',
            }}
          >
            {segments.map((seg, i) => {
              const def = STATE_DEFS[seg.state] || STATE_DEFS.planned;
              const pct = totalMs > 0 ? (seg.duration / totalMs) * 100 : 100;
              const isLast = i === segments.length - 1;
              const isWide = pct > 10 && !isMobile;
              const Icon = def.icon;
              return (
                <Box
                  key={i}
                  data-testid={`timeline-segment-${i}`}
                  onMouseEnter={isMobile ? undefined : () => setHovered(i)}
                  onMouseLeave={isMobile ? undefined : () => setHovered(null)}
                  onClick={isMobile ? () => setHovered(hovered === i ? null : i) : undefined}
                  sx={{
                    flex: `${Math.max(pct, 4)} 1 0`,
                    minWidth: '8px',
                    bgcolor: def.color,
                    borderRight: isLast ? 'none' : '2px solid #fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    px: isWide ? 1.25 : 0.5,
                    gap: 0.75,
                    color: '#fff',
                    position: 'relative',
                    cursor: 'default',
                    overflow: 'hidden',
                    transition: 'filter 150ms',
                    filter: hovered !== null && hovered !== i ? 'brightness(0.85) saturate(0.7)' : 'none',
                  }}
                >
                  {seg.rollback && (
                    <Box sx={{ position: 'absolute', top: 3, left: 5, fontSize: 9, opacity: 0.9, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                      <UndoIcon sx={{ fontSize: 10 }} />
                      <span>{t('timeline.rollback')}</span>
                    </Box>
                  )}
                  {isWide && (
                    <>
                      <Icon sx={{ fontSize: 16, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#fff' }}>
                          {t(`status.${seg.state}`)}
                        </Typography>
                        <Typography sx={{ fontSize: 10, opacity: 0.85, color: '#fff' }}>
                          {formatDurationLong(seg.duration, t)}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {isLast && (
                    <Box
                      sx={{
                        position: 'absolute', right: -1, top: 0, bottom: 0, width: 2,
                        bgcolor: '#fff', boxShadow: `0 0 0 2px ${def.color}`, opacity: 0.9,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Hover detail row */}
          <Box data-testid="timeline-hover-detail" sx={{ mt: 1, minHeight: 18, fontSize: 11, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
            {hovered !== null ? (
              <>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: (STATE_DEFS[segments[hovered].state] || STATE_DEFS.planned).color, flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 600, fontSize: 11, color: 'text.primary' }}>
                  {t(`status.${segments[hovered].state}`)}
                </Typography>
                <Typography sx={{ fontSize: 11 }}>·</Typography>
                <Typography sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                  {shortDateTime(segments[hovered].start)} → {segments[hovered].end >= now ? t('timeline.now') : shortDateTime(segments[hovered].end)}
                </Typography>
                <Typography sx={{ fontSize: 11 }}>·</Typography>
                <Typography sx={{ fontSize: 11 }}>{formatDurationLong(segments[hovered].duration, t)}</Typography>
                {segments[hovered].from_state && (
                  <>
                    <Typography sx={{ fontSize: 11 }}>·</Typography>
                    <Typography sx={{ fontSize: 11, fontStyle: 'italic' }}>
                      {t(`status.${segments[hovered].from_state}`)} → {t(`status.${segments[hovered].state}`)}
                    </Typography>
                  </>
                )}
                {segments[hovered].rollback && (
                  <Typography sx={{ color: 'error.main', fontWeight: 600, fontSize: 11, ml: 'auto' }}>
                    ↶ {t('timeline.rollback')}
                  </Typography>
                )}
              </>
            ) : (
              <Typography sx={{ color: 'text.disabled', fontSize: 11 }}>
                {isMobile ? t('timeline.tapHint') : t('timeline.hoverHint')}
              </Typography>
            )}
          </Box>

          {/* Per-state legend */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.75, mt: 1.75, pt: 1.75, borderTop: '1px dashed', borderColor: 'divider' }}>
            {CANONICAL_ORDER.filter(s => perState[s]).map(s => {
              const def = STATE_DEFS[s] || STATE_DEFS.planned;
              return (
                <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: def.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{t(`status.${s}`)}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    {formatDurationLong(perState[s], t)}
                  </Typography>
                  {visitCount[s] > 1 && (
                    <Box sx={{ fontSize: 10, color: 'text.secondary', px: 0.75, py: '1px', borderRadius: '8px', bgcolor: def.bg, fontWeight: 600 }}>
                      × {visitCount[s]}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Summary strip */}
        <Box
          data-testid="timeline-summary"
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <SummaryStat
            icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
            tone="primary.main"
            label={t('timeline.activeWorkTime')}
            value={formatDurationLong(activeMs, t)}
            sub={t('timeline.workPeriods', { count: workPeriodCount })}
          />
          <SummaryStat
            icon={<HourglassTopIcon sx={{ fontSize: 16 }} />}
            tone={currentDef.color}
            label={t('timeline.inCurrentState')}
            value={formatDurationLong(inCurrentMs, t)}
            sub={t(`status.${current.state}`).toLowerCase()}
            borderLeft={!isMobile}
          />
          <SummaryStat
            icon={<EventRepeatIcon sx={{ fontSize: 16 }} />}
            tone="#7c3aed"
            label={t('timeline.totalTime')}
            value={formatDurationLong(totalMs, t)}
            sub={t('timeline.sinceCreation')}
            borderLeft={!isMobile}
            borderTop={isMobile}
          />
          <SummaryStat
            icon={<SyncIcon sx={{ fontSize: 16 }} />}
            tone="#0891b2"
            label={t('timeline.stateChanges')}
            value={String(events.length)}
            sub={t('timeline.lastChanged', { time: relativeFromNow(current.at, now, t) })}
            borderLeft={!isMobile}
            borderTop={isMobile}
            last
          />
        </Box>
      </Collapse>
    </Paper>
  );
}

function SummaryStat({ icon, tone, label, value, sub, last, borderLeft, borderTop }) {
  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 2.25 },
        py: 1.75,
        borderRight: last ? 'none' : '1px solid',
        borderRightColor: 'divider',
        ...(borderLeft && { borderLeft: '1px solid', borderLeftColor: 'divider' }),
        ...(borderTop && { borderTop: '1px solid', borderTopColor: 'divider' }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box sx={{ color: tone }}>{icon}</Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', fontFamily: "'Plus Jakarta Sans', monospace" }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
        {sub}
      </Typography>
    </Box>
  );
}
